import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'sonner';

// Layouts
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';

// Pages
import PublicPortal from './pages/PublicPortal';
import Login from './pages/Login';
import Register from './pages/Register';
import CitizenDashboard from './pages/CitizenDashboard';
import AgentDashboard from './pages/AgentDashboard';
import DirectionDashboard from './pages/DirectionDashboard';
import CMSRealisations from './pages/CMSRealisations';
import DemandeAide from './pages/DemandeAide';
import SignalerProbleme from './pages/SignalerProbleme';
import TrouverService from './pages/TrouverService';
import Realisations from './pages/Realisations';
import RealisationDetails from './pages/RealisationDetails';
import SuiviDossier from './pages/SuiviDossier';
import RecipisseDemande from './pages/RecipisseDemande';
import Confidentialite from './pages/Confidentialite';
import CMSServices from './pages/CMSServices';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();
  
  if (user === null) return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  
  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  const getDashboardRedirect = () => {
    if (!user) return '/';
    if (user.role === 'admin' || user.role === 'direction') return '/direction';
    if (user.role === 'agent') return '/agent';
    return '/citoyen';
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 text-slate-900 font-sans">
      <Navbar />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<PublicPortal />} />
          <Route path="/realisations" element={<Realisations />} />
          <Route path="/realisations/:id" element={<RealisationDetails />} />
          <Route path="/suivi" element={<SuiviDossier />} />
          <Route path="/login" element={user === null ? <div className="min-h-screen flex items-center justify-center">Chargement...</div> : user ? <Navigate to={getDashboardRedirect()} replace /> : <Login />} />
          <Route path="/register" element={user === null ? <div className="min-h-screen flex items-center justify-center">Chargement...</div> : user ? <Navigate to={getDashboardRedirect()} replace /> : <Register />} />
          
          <Route path="/trouver-service" element={<TrouverService />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          
          {/* Protected Routes */}
          <Route path="/citoyen/*" element={
            <ProtectedRoute allowedRoles={['citoyen', 'admin']}>
              <CitizenDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/demander-aide" element={
            <ProtectedRoute allowedRoles={['citoyen', 'admin']}>
              <DemandeAide />
            </ProtectedRoute>
          } />
          
          <Route path="/recipisse/:id" element={
            <ProtectedRoute allowedRoles={['citoyen', 'admin']}>
              <RecipisseDemande />
            </ProtectedRoute>
          } />

          <Route path="/signaler" element={
            <ProtectedRoute allowedRoles={['citoyen', 'admin']}>
              <SignalerProbleme />
            </ProtectedRoute>
          } />
          
          <Route path="/agent/*" element={
            <ProtectedRoute allowedRoles={['agent', 'admin', 'direction']}>
              <AgentDashboard />
            </ProtectedRoute>
          } />
          
          <Route path="/direction/*" element={
            <ProtectedRoute allowedRoles={['direction', 'admin']}>
              <DirectionDashboard />
            </ProtectedRoute>
          } />

          <Route path="/admin/realisations" element={
            <ProtectedRoute allowedRoles={['admin', 'direction']}>
              <CMSRealisations />
            </ProtectedRoute>
          } />

          <Route path="/admin/services" element={
            <ProtectedRoute allowedRoles={['admin', 'direction']}>
              <CMSServices />
            </ProtectedRoute>
          } />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
